import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/pt';
import 'dayjs/locale/en-gb';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ptPT, enUS } from '@mui/x-date-pickers/locales';
import { useFormik } from 'formik';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Grid2 as Grid,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import {
    getCountryOptions,
    genderOptions,
    getTimezoneOptions,
    localeOptions,
    roleLabels,
    sexOptions
} from '../../../configs/selfProfile';
import {
    buildProfilePatch,
    profileSchema,
    profileValues
} from '../../../utils/selfProfile';
import { profileErrorMessage, updateOwnProfile } from '../../../services/selfProfile';
import { useProfileAccount } from './useProfileAccount';
import i18n, { t } from './translations';
import ProfileSection from './ProfileSection';
import PasswordForm from './PasswordForm';
import ProfilePhoto from './ProfilePhoto';
export default function Profile() {
    useTranslation('selfProfile', { useSuspense: false });
    const account = useProfileAccount();
    if (!account.user.uuid || !account.role)
        return (
            <Alert severity="info">
                {t('Selecione um perfil válido para consultar os seus dados.')}
            </Alert>
        );
    return <ProfileContent key={account.contextKey} account={account} />;
}
function ProfileContent({ account }) {
    const language = i18n.resolvedLanguage;
    const countries = useMemo(() => getCountryOptions(language), [language]);
    const { user, role, refreshUser } = account;
    const initial = useMemo(() => profileValues(user, role), [user, role]);
    const [notice, setNotice] = useState(null);
    const [refreshPending, setRefreshPending] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const timezoneOptions = useMemo(
        () => getTimezoneOptions(user.timezone),
        [user.timezone]
    );
    const locales =
        localeOptions.some((option) => option.value === user.locale) || !user.locale
            ? localeOptions
            : [
                  ...localeOptions,
                  {
                      value: user.locale,
                      label: user.locale
                  }
              ];
    const formik = useFormik({
        initialValues: initial,
        enableReinitialize: true,
        validationSchema: profileSchema(initial),
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            setNotice(null);
            let saved = false;
            try {
                const payload = buildProfilePatch(values, initial);
                if (!Object.keys(payload).length) return;
                await updateOwnProfile(payload, role);
                saved = true;
                resetForm({
                    values
                });
                await refreshUser();
                setRefreshPending(false);
                setNotice({
                    severity: 'success',
                    text: 'Os dados do perfil foram guardados.'
                });
            } catch (error) {
                if (saved) {
                    setRefreshPending(true);
                    setNotice({
                        severity: 'warning',
                        text: 'Os dados foram guardados, mas não foi possível atualizar a informação apresentada. Tente atualizar o perfil.'
                    });
                } else {
                    setNotice({
                        severity: 'error',
                        text: profileErrorMessage(error)
                    });
                }
            } finally {
                setSubmitting(false);
            }
        }
    });
    const busy = formik.isSubmitting || refreshing;
    const field = (name) => ({
        name,
        value: formik.values[name],
        onChange: formik.handleChange,
        onBlur: formik.handleBlur,
        error: Boolean(formik.touched[name] && formik.errors[name]),
        helperText: t(formik.touched[name] && formik.errors[name]),
        disabled: busy || refreshPending,
        fullWidth: true
    });
    const refresh = async () => {
        setRefreshing(true);
        try {
            await refreshUser();
            setRefreshPending(false);
            setNotice({
                severity: 'success',
                text: 'O perfil foi atualizado.'
            });
        } catch {
            setNotice({
                severity: 'warning',
                text: 'Os dados estão guardados, mas ainda não foi possível atualizar o perfil. Tente novamente.'
            });
        } finally {
            setRefreshing(false);
        }
    };
    return (
        <Box
            sx={{
                maxWidth: 1280,
                mx: 'auto',
                py: { xs: 2, md: 3 }
            }}
        >
            <Paper
                variant="outlined"
                sx={{
                    p: {
                        xs: 2.5,
                        md: 3
                    },
                    mb: 3,
                    borderRadius: 3
                }}
            >
                <Stack
                    direction={{
                        xs: 'column',
                        sm: 'row'
                    }}
                    spacing={2.5}
                    alignItems={{
                        xs: 'flex-start',
                        sm: 'center'
                    }}
                >
                    <ProfilePhoto />
                    <Box
                        sx={{
                            minWidth: 0,
                            flex: 1
                        }}
                    >
                        <Typography
                            variant="h2"
                            component="h2"
                            sx={{
                                color: 'primary.main',
                                overflowWrap: 'anywhere'
                            }}
                        >
                            {user.name || t('O meu perfil')}
                        </Typography>
                        <Typography
                            sx={{
                                mt: 0.75,
                                overflowWrap: 'anywhere'
                            }}
                        >
                            {user.email}
                        </Typography>
                        <Chip
                            label={t(roleLabels[role.name] || role.name)}
                            size="small"
                            sx={{
                                mt: 1.5,
                                bgcolor: 'secondary.200',
                                color: 'primary.main'
                            }}
                        />
                    </Box>
                    {role.account?.name && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                {t('Clínica')}
                            </Typography>
                            <Typography
                                sx={{
                                    mt: 0.5
                                }}
                            >
                                {role.account.name}
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </Paper>
            <Grid container spacing={3} alignItems="flex-start">
                <Grid
                    size={{
                        xs: 12,
                        lg: 8
                    }}
                >
                    <Stack
                        component="form"
                        noValidate
                        onSubmit={formik.handleSubmit}
                        spacing={3}
                    >
                        <ProfileSection title={t('Dados pessoais')}>
                            <Grid container spacing={2.5}>
                                <Grid size={12}>
                                    <TextField
                                        label={t('Nome completo')}
                                        autoComplete="name"
                                        {...field('name')}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6
                                    }}
                                >
                                    <TextField
                                        label={t('Email')}
                                        value={user.email || ''}
                                        fullWidth
                                        InputProps={{
                                            readOnly: true
                                        }}
                                        helperText={t(
                                            'O email de acesso não pode ser alterado nesta página.'
                                        )}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6
                                    }}
                                >
                                    <LocalizationProvider
                                        dateAdapter={AdapterDayjs}
                                        adapterLocale={language === 'en' ? 'en-gb' : 'pt'}
                                        localeText={
                                            (language === 'en' ? enUS : ptPT).components
                                                .MuiLocalizationProvider.defaultProps
                                                .localeText
                                        }
                                    >
                                        <DatePicker
                                            label={t('Data de nascimento')}
                                            format="DD/MM/YYYY"
                                            value={
                                                formik.values.birth_date
                                                    ? dayjs(formik.values.birth_date)
                                                    : null
                                            }
                                            onChange={(value) =>
                                                formik.setFieldValue(
                                                    'birth_date',
                                                    value
                                                        ? value.format('YYYY-MM-DD')
                                                        : ''
                                                )
                                            }
                                            disableFuture
                                            disabled={busy || refreshPending}
                                            slotProps={{
                                                textField: {
                                                    name: 'birth_date',
                                                    fullWidth: true,
                                                    onBlur: () =>
                                                        formik.setFieldTouched(
                                                            'birth_date',
                                                            true
                                                        ),
                                                    error: Boolean(
                                                        formik.touched.birth_date &&
                                                            formik.errors.birth_date
                                                    ),
                                                    helperText: t(
                                                        formik.touched.birth_date &&
                                                            formik.errors.birth_date
                                                    )
                                                },
                                                openPickerButton: {
                                                    'aria-label': t(
                                                        'Escolher data de nascimento'
                                                    )
                                                }
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6
                                    }}
                                >
                                    <TextField
                                        label={t('Sexo legal')}
                                        select
                                        {...field('sex')}
                                    >
                                        {sexOptions.map((option) => (
                                            <MenuItem
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {t(option.label)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6
                                    }}
                                >
                                    <TextField
                                        label={t('Género')}
                                        select
                                        {...field('gender')}
                                    >
                                        {genderOptions.map((option) => (
                                            <MenuItem
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {t(option.label)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            </Grid>
                        </ProfileSection>
                        <ProfileSection
                            title={t('Contactos e morada')}
                            description={t(
                                'Informação associada ao seu perfil de acesso a este portal.'
                            )}
                        >
                            <Grid container spacing={2.5}>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6
                                    }}
                                >
                                    <TextField
                                        label={t('Telefone')}
                                        type="tel"
                                        autoComplete="tel"
                                        {...field('phone')}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <TextField
                                        label={t('Morada')}
                                        autoComplete="street-address"
                                        {...field('street')}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6
                                    }}
                                >
                                    <TextField
                                        label={t('Código postal')}
                                        autoComplete="postal-code"
                                        {...field('zip')}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6
                                    }}
                                >
                                    <TextField
                                        label={t('Localidade')}
                                        autoComplete="address-level2"
                                        {...field('city')}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6
                                    }}
                                >
                                    <TextField
                                        label={t('Distrito / região')}
                                        autoComplete="address-level1"
                                        {...field('province')}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6
                                    }}
                                >
                                    <Autocomplete
                                        options={countries}
                                        value={
                                            countries.find(
                                                (option) =>
                                                    option.value === formik.values.country
                                            ) || null
                                        }
                                        getOptionLabel={(option) => option.label}
                                        isOptionEqualToValue={(a, b) =>
                                            a.value === b.value
                                        }
                                        disabled={busy || refreshPending}
                                        disableClearable={Boolean(initial.country)}
                                        onChange={(_, option) =>
                                            formik.setFieldValue(
                                                'country',
                                                option?.value || ''
                                            )
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t('País')}
                                                error={Boolean(
                                                    formik.touched.country &&
                                                        formik.errors.country
                                                )}
                                                helperText={
                                                    formik.touched.country &&
                                                    t(formik.errors.country)
                                                }
                                                onBlur={() =>
                                                    formik.setFieldTouched(
                                                        'country',
                                                        true
                                                    )
                                                }
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </ProfileSection>
                        <ProfileSection title={t('Preferências da conta')}>
                            <Grid container spacing={2.5}>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6
                                    }}
                                >
                                    <TextField
                                        label={t('Idioma da conta')}
                                        select
                                        {...field('locale')}
                                        helperText={
                                            (formik.touched.locale &&
                                                t(formik.errors.locale)) ||
                                            t('Preferência guardada na sua conta.')
                                        }
                                    >
                                        {!initial.locale && (
                                            <MenuItem value="">
                                                {t('Não indicado')}
                                            </MenuItem>
                                        )}
                                        {locales.map((option) => (
                                            <MenuItem
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {t(option.label)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6
                                    }}
                                >
                                    <Autocomplete
                                        options={timezoneOptions}
                                        value={formik.values.timezone || null}
                                        disabled={busy || refreshPending}
                                        disableClearable={Boolean(initial.timezone)}
                                        onChange={(_, value) =>
                                            formik.setFieldValue('timezone', value || '')
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t('Fuso horário')}
                                                error={Boolean(
                                                    formik.touched.timezone &&
                                                        formik.errors.timezone
                                                )}
                                                helperText={
                                                    formik.touched.timezone &&
                                                    t(formik.errors.timezone)
                                                }
                                                onBlur={() =>
                                                    formik.setFieldTouched(
                                                        'timezone',
                                                        true
                                                    )
                                                }
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </ProfileSection>
                        {notice && (
                            <Alert
                                severity={notice.severity}
                                action={
                                    refreshPending ? (
                                        <Button
                                            color="inherit"
                                            size="small"
                                            disabled={busy}
                                            onClick={refresh}
                                        >
                                            {t('Atualizar perfil')}
                                        </Button>
                                    ) : undefined
                                }
                            >
                                {t(notice.text)}
                            </Alert>
                        )}
                        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                            <Button
                                type="button"
                                disabled={busy || refreshPending || !formik.dirty}
                                onClick={() => {
                                    formik.resetForm();
                                    setNotice(null);
                                }}
                            >
                                {t('Cancelar alterações')}
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={busy || refreshPending || !formik.dirty}
                            >
                                {formik.isSubmitting
                                    ? t('A guardar…')
                                    : t('Guardar alterações')}
                            </Button>
                        </Stack>
                    </Stack>
                </Grid>
                <Grid
                    size={{
                        xs: 12,
                        lg: 4
                    }}
                >
                    <PasswordForm />
                </Grid>
            </Grid>
        </Box>
    );
}

ProfileContent.propTypes = { account: PropTypes.object.isRequired };
